import { ReactNode, useCallback, useState } from 'react';

import { nextAsyncQueue } from '@vimeo/async/wait';
import { Paragraph } from '@vimeo/bokeh';
import { createInteractionDomName } from '@vimeo/cloud-live/libs/shared/utils/dom/identifying';
import { translations } from '@vimeo/cloud-live/translations';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { EInteractionAuthOption } from '@vimeo/live-core/types/interaction_room';
import { LoginJoinModal } from '@vimeo/ui/core/form/LoginJoinModal';
import { Viewer, useViewer } from '@vimeo/viewer';

import { useInteractionLoginOption } from '../shared/useInteractionLoginOption';

type TLoginType = 'login' | 'join';

interface ILoginButtonProps {
  id: string;
  label: string;
  isDisabled: boolean;
  onClick: () => void;
}

interface IVimeoUserLoginProps extends IBaseDomElementProps {
  buttonRenderer: (props: ILoginButtonProps) => ReactNode;
  loginType?: EInteractionAuthOption;
}

/**
 * Generic handler of vimeo login with modal forms.
 */
export function VimeoUserLogin({
  id = createInteractionDomName('vimeo-user-login'),
  loginType = EInteractionAuthOption.VIMEO,
  buttonRenderer,
}: IVimeoUserLoginProps) {
  const [isActive, setActive] = useState<boolean>(false);
  const [type, setType] = useState<TLoginType>('login');

  const viewer: Viewer = useViewer();
  const { isLoading, isLoginFailed, errorMessage, onLogin } = useInteractionLoginOption(loginType);

  const vimeoLoginRequired: boolean = Boolean(viewer && !viewer?.user);

  const onAuthClicked = useCallback(() => onLogin(), [onLogin]);

  const onModalClose = useCallback(() => setActive(false), []);

  const onModalOpen = useCallback(() => setActive(true), []);

  const onVimeoLoginSuccess = useCallback(async () => {
    if (viewer.refreshJwt) {
      await viewer.refreshJwt();
      await nextAsyncQueue();
      await onLogin();
    }
    setActive(false);
  }, [viewer, onLogin]);

  return (
    <>
      {vimeoLoginRequired ? (
        <LoginJoinModal
          type={type}
          xsrft={viewer.xsrft}
          isShowing={isActive}
          onDismiss={onModalClose}
          onSuccess={onVimeoLoginSuccess}
          redirectUrl={window.location.href}
          redirectUrlAfterSocialLogin={window.location.href}
          updateInitialType={setType}
        />
      ) : null}

      {buttonRenderer({
        id: createInteractionDomName(id, 'button'),
        label: translations.loginWithVimeo,
        isDisabled: isLoading,
        onClick: vimeoLoginRequired ? onModalOpen : onAuthClicked,
      })}

      {isLoginFailed ? (
        <Paragraph id={createInteractionDomName(id, 'error')} size={'xs'} color={'red.500'}>
          {errorMessage}
        </Paragraph>
      ) : null}
    </>
  );
}
